import type { ChangeNotification, SoapResponseContext } from '@zimbra-webif/types';

export type NotificationListener = (n: ChangeNotification) => void;

/**
 * Fan-out hub for change notifications.
 *
 * Every SOAP response is passed through `ingest()`; any `notify[]` blocks are
 * emitted to subscribers (the UI cache layer) and the highest seq is returned
 * so the client can ack it on the next request. This is the backbone of
 * real-time mail/calendar refresh — no polling-the-server-manually hacks.
 */
export class NotificationHub {
  private readonly listeners = new Set<NotificationListener>();

  /** Subscribe; returns an unsubscribe function. */
  on(listener: NotificationListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(notification: ChangeNotification): void {
    for (const listener of this.listeners) {
      listener(notification);
    }
  }

  /**
   * Process a response context: emit each notify block, return the highest seq
   * seen (or `undefined` if there were none).
   */
  ingest(context: SoapResponseContext | undefined): number | undefined {
    const blocks = context?.notify;
    if (!blocks?.length) {
      return undefined;
    }
    let maxSeq: number | undefined;
    for (const block of blocks) {
      this.emit(block);
      if (maxSeq === undefined || block.seq > maxSeq) {
        maxSeq = block.seq;
      }
    }
    return maxSeq;
  }
}
